'use strict';

export class TeejitRestClient {
    static pendingRequests = new Map();
    static pendingControllers = new Map();
    static responseCache = new Map();
    static responseCacheTtl = 30000;

    static cacheStore() {
        if (typeof window === 'undefined') {
            return {
                pendingRequests: TeejitRestClient.pendingRequests,
                pendingControllers: TeejitRestClient.pendingControllers,
                responseCache: TeejitRestClient.responseCache,
            };
        }

        if (!window.__khRestClientCacheStore) {
            window.__khRestClientCacheStore = {
                pendingRequests: new Map(),
                pendingControllers: new Map(),
                responseCache: new Map(),
            };
        }
        if (!window.__khRestClientCacheStore.pendingControllers) {
            window.__khRestClientCacheStore.pendingControllers = new Map();
        }

        return window.__khRestClientCacheStore;
    }

    constructor({restBaseUrl, accessToken, nonce} = {}) {
        this.restBaseUrl = String(restBaseUrl || window.khThemeRuntime?.restBaseUrl || '/wp-json/knowhub/v2').replace(/\/+$/, '');
        this.accessToken = String(accessToken || window.khThemeRuntime?.userAccessToken || '');
        this.nonce = String(nonce || window.khThemeRuntime?.restNonce || '');
        this.frontendLocale = String(window.khThemeRuntime?.frontendLocale || 'de_DE').trim();
    }

    getFrontendLocale() {
        return String(window.khThemeRuntime?.frontendLocale || this.frontendLocale || 'de_DE').trim();
    }

    setFrontendLocale(locale) {
        this.frontendLocale = String(locale || '').trim();
    }

    headers(isJson = false) {
        const frontendLocale = this.getFrontendLocale();
        const headers = {
            Accept: 'application/json',
        };

        if (isJson) {
            headers['Content-Type'] = 'application/json';
            if (this.nonce) {
                headers['X-WP-Nonce'] = this.nonce;
            }
        }

        if (this.accessToken) {
            headers.Authorization = 'Bearer ' + this.accessToken;
        }

        if (frontendLocale) {
            headers['X-Frontend-Locale'] = frontendLocale;
        }

        return headers;
    }

    url(path) {
        return this.withFrontendLocale(this.restBaseUrl + '/' + String(path || '').replace(/^\/+/, ''));
    }

    withFrontendLocale(url) {
        const frontendLocale = this.getFrontendLocale();
        if (!frontendLocale || /[?&](frontend_locale|locale)=/.test(url)) {
            return url;
        }

        const separator = url.includes('?') ? '&' : '?';
        return url + separator + 'frontend_locale=' + encodeURIComponent(frontendLocale);
    }

    withCacheBuster(url) {
        const separator = url.includes('?') ? '&' : '?';
        return url + separator + '_khcb=' + Date.now();
    }

    async requestJson(path, {method = 'GET', body = null, isJson = false, noCache = false, signal = null, preserveReadCaches = false} = {}) {
        const normalizedMethod = String(method || 'GET').toUpperCase();
        const baseUrl = this.url(path);
        const requestUrl = noCache && normalizedMethod === 'GET'
            ? this.withCacheBuster(baseUrl)
            : baseUrl;
        const cacheable = normalizedMethod === 'GET' && body === null && noCache !== true;
        const cacheKey = cacheable ? this.cacheKey(requestUrl) : '';
        const cacheStore = TeejitRestClient.cacheStore();

        if (cacheable) {
            const cached = cacheStore.responseCache.get(cacheKey);
            if (cached && Date.now() - cached.createdAt < TeejitRestClient.responseCacheTtl) {
                return cached.payload;
            }

            const pending = cacheStore.pendingRequests.get(cacheKey);
            if (pending) {
                return pending;
            }
        }

        // An invalidated read may still be settling when the next read of the same URL starts.
        // Track their controllers separately so both remain abortable during a locale change.
        const requestId = normalizedMethod + ':' + requestUrl + ':' + Date.now() + ':' + Math.random().toString(36).slice(2);
        const controller = !signal && typeof window !== 'undefined' && typeof window.AbortController === 'function'
            ? new window.AbortController()
            : null;
        const requestSignal = signal || controller?.signal;

        if (controller) {
            cacheStore.pendingControllers.set(requestId, controller);
        }

        const requestPromise = fetch(requestUrl, {
            method: normalizedMethod,
            credentials: 'same-origin',
            cache: noCache ? 'no-store' : 'default',
            headers: this.headers(isJson),
            body: body === null ? null : JSON.stringify(body),
            ...(requestSignal ? {signal: requestSignal} : {}),
        }).then(async (response) => {
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                const error = new Error('kh_rest_request_failed');
                error.status = response.status;
                error.payload = payload;
                throw error;
            }

            if (cacheable) {
                // Only the read still registered for this key may populate the cache. A write,
                // explicit invalidation or abort can have retired this request in the meantime.
                if (cacheStore.pendingRequests.get(cacheKey) === requestPromise && !requestSignal?.aborted) {
                    cacheStore.responseCache.set(cacheKey, {
                        createdAt: Date.now(),
                        payload,
                    });
                }
            } else if (!preserveReadCaches || normalizedMethod !== 'GET') {
                TeejitRestClient.invalidateRuntimeCaches();
            }

            return payload;
        }).finally(() => {
            if (cacheable && cacheStore.pendingRequests.get(cacheKey) === requestPromise) {
                cacheStore.pendingRequests.delete(cacheKey);
            }
            cacheStore.pendingControllers.delete(requestId);
        });

        if (cacheable) {
            cacheStore.pendingRequests.set(cacheKey, requestPromise);
        }

        return requestPromise;
    }

    async requestHtml(path, options = {}) {
        return TeejitRestClient.normalizeHtml(await this.requestJson(path, options));
    }

    cacheKey(url) {
        return TeejitRestClient.normalizeCacheUrl(url) + '|' + this.accessToken;
    }

    invalidateCacheForUrl(url) {
        const prefix = TeejitRestClient.normalizeCacheUrl(url) + '|';
        const cacheStore = TeejitRestClient.cacheStore();
        new Set([...cacheStore.responseCache.keys(), ...cacheStore.pendingRequests.keys()]).forEach((key) => {
            if (key.startsWith(prefix)) {
                cacheStore.responseCache.delete(key);
                cacheStore.pendingRequests.delete(key);
            }
        });
    }

    static clearResponseCache() {
        const cacheStore = TeejitRestClient.cacheStore();
        cacheStore.responseCache.clear();
        cacheStore.pendingRequests.clear();
        TeejitRestClient.responseCache.clear();
        TeejitRestClient.pendingRequests.clear();
    }

    static abortPendingRequests(reason = 'kh_rest_requests_aborted') {
        const cacheStore = TeejitRestClient.cacheStore();
        cacheStore.pendingControllers.forEach((controller) => {
            if (controller && typeof controller.abort === 'function') {
                controller.abort(reason);
            }
        });
        cacheStore.pendingControllers.clear();
        cacheStore.pendingRequests.clear();
        TeejitRestClient.pendingRequests.clear();
    }

    static isAbortError(error) {
        return error?.name === 'AbortError'
            || error?.message === 'AbortError'
            || error === 'frontend-locale-change'
            || error === 'kh_rest_requests_aborted';
    }

    static invalidateRuntimeCaches() {
        TeejitRestClient.clearResponseCache();

        if (typeof window !== 'undefined') {
            window.khSessionWarmupPromise = null;
        }
    }

    static normalizeCacheUrl(url) {
        if (typeof window === 'undefined' || typeof window.URL !== 'function') {
            return String(url || '');
        }

        try {
            const parsed = new URL(String(url || ''), window.location.origin);
            return parsed.origin + parsed.pathname.replace(/\/+$/, '') + parsed.search;
        } catch (error) {
            return String(url || '');
        }
    }

    static normalizeHtml(payload) {
        if (typeof payload === 'string') {
            return payload;
        }
        if (payload && typeof payload.html === 'string') {
            return payload.html;
        }
        if (payload && typeof payload.data === 'string') {
            return payload.data;
        }
        return '';
    }
}

if (typeof document !== 'undefined' && !document.documentElement.dataset.khRestClientAbortBound) {
    document.documentElement.dataset.khRestClientAbortBound = '1';
    document.addEventListener('kh:frontendLocaleChanging', () => {
        TeejitRestClient.abortPendingRequests('frontend-locale-change');
    });
}
